import React from 'react'
import { SmartTooltip } from '../../components/SmartTooltip/SmartTooltip'

/**
 * Значение длиннее этого сворачивается; такой длины и короче показывается целиком.
 * Граница — длина UUID v5 (36 символов): собственные идентификаторы системы помещаются
 * целиком, сворачивается только то, что длиннее любого нашего токена.
 */
const MAX_INLINE_LENGTH = 36
/** Сколько символов остаётся от начала и от конца значения. */
const HEAD_LENGTH = 15
const TAIL_LENGTH = 15

/** Знак пропуска: точки на базовой линии, разрежённые `ELLIPSIS_TRACKING`. */
export const ELLIPSIS = '.....'
/** Зазор между точками — заметно уже обычного пробела, но одинаковый. */
const ELLIPSIS_TRACKING = '0.18em'
/** Воздух от знака пропуска до самого значения. */
const ELLIPSIS_GAP = '0.18rem'

/**
 * Делит значение на видимые начало и конец. Сворачивается именно середина: у идентификатора,
 * который задаёт мерчант, уникальным может быть и префикс, и окончание, поэтому два разных
 * длинных значения обязаны оставаться различимы на глаз.
 * Возвращает `null`, если значение и так помещается целиком.
 */
export const splitForDisplay = (value: string): { head: string; tail: string } | null =>
  value.length > MAX_INLINE_LENGTH
    ? { head: value.slice(0, HEAD_LENGTH), tail: value.slice(-TAIL_LENGTH) }
    : null

type Props = {
  value: string | null | undefined
  className?: string
  /** Что показать вместо пустого значения. */
  empty?: React.ReactNode
  /**
   * Делать ли свёрнутое значение самостоятельной точкой фокуса. Внутри ссылки или кнопки
   * ставить `false`: вложенный интерактивный элемент ломает переход по табу, а полное
   * значение там всё равно открывается по наведению.
   */
  focusable?: boolean
}

// Длинное значение без пробелов (внешний ID клиента — до 255 символов) не переносится
// и растягивает контейнер по горизонтали, утаскивая за экран всю страницу. Компонент
// показывает начало и конец значения, а целиком отдаёт его по наведению или клику —
// в тултипе, который переносит текст по любому символу и всегда остаётся внутри окна.
export const TruncatedValue: React.FC<Props> = ({
  value,
  className,
  empty = '',
  focusable = true,
}) => {
  if (!value) return <>{empty}</>

  const parts = splitForDisplay(value)
  if (!parts) return <span className={className}>{value}</span>

  const interactiveProps = focusable
    ? {
        tabIndex: 0,
        role: 'button',
        'aria-label': 'Показать значение целиком',
        // Клик по значению даёт ему фокус — SmartTooltip открывается по focus так же,
        // как по наведению, поэтому работают оба способа. Всплытие гасится: значение
        // может стоять внутри строки-аккордеона, и тогда попытка прочитать его целиком
        // сворачивала бы саму строку.
        onClick: (e: React.MouseEvent<HTMLSpanElement>) => {
          e.stopPropagation()
          e.currentTarget.focus()
        },
      }
    : {}

  return (
    <SmartTooltip content={value}>
      <span
        className={`d-inline-flex align-items-center ${className ?? ''}`}
        style={{ cursor: 'pointer', minWidth: 0, whiteSpace: 'nowrap' }}
        {...interactiveProps}
      >
        <span>{parts.head}</span>
        {/* Знак пропуска отбивается воздухом и приглушённым цветом: вплотную и в цвет текста
            он читается как часть самого значения. `letterSpacing` вешает зазор и после
            последней точки — гасим его отрицательным правым отступом, иначе воздух слева и
            справа от группы получается разный. */}
        <span
          className='text-gray-500'
          style={{
            letterSpacing: ELLIPSIS_TRACKING,
            marginLeft: ELLIPSIS_GAP,
            marginRight: `calc(${ELLIPSIS_GAP} - ${ELLIPSIS_TRACKING})`,
          }}
          aria-hidden='true'
        >
          {ELLIPSIS}
        </span>
        <span>{parts.tail}</span>
      </span>
    </SmartTooltip>
  )
}

export default TruncatedValue
